import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Filters } from '@ui/src';
import { OrganizationType, CardsTransactions } from '../../types';
import {
  CardStatus,
  CustomerCard,
  OrderCardFormValues,
} from '../../cards/types';

export interface CustomerCards {
  cardsTransactionsIsLoading: boolean;
  values?: CardsTransactions[];
  cardId?: string;
  sort?: {
    sortProp: string;
    sortDir: string;
  };
  filters?: Filters;
  requestInProgress: boolean;
  customerType?: string;
  cards?: CustomerCard[];
  isLoading: boolean;
  statusChangeInProgress?: boolean;
  updatedCard?: {
    id: string;
    status: CardStatus;
  };
  cardInfo?: CustomerCard;
  orderCardFormValues?: OrderCardFormValues;
  cardSubmitInProgress?: boolean;
  cardSubmitSuccess?: boolean;
  changeStatusSuccess?: boolean;
  cardInfoLoading?: boolean;
}

const initialState: CustomerCards = {
  cardsTransactionsIsLoading: true,
  requestInProgress: false,
  values: undefined,
  isLoading: true,
};

export const customerCardsSlice = createSlice({
  name: 'customerCards',
  initialState,
  reducers: {
    getCardsTransactions: (
      state,
      action: PayloadAction<{
        name?: string;
        direction?: 'asc' | 'desc';
        filters?: Filters;
        cardId: string;
        customerType: OrganizationType;
      }>,
    ) => {
      state.cardId = action.payload.cardId;
      state.requestInProgress = true;
      state.customerType = action.payload.customerType;

      if (action.payload.name && action.payload.direction) {
        state.sort = {
          sortDir: action.payload.direction,
          sortProp: action.payload.name,
        };
      }

      if (action.payload.filters) {
        state.filters = action.payload.filters;
      }
    },
    getCardsTransactionsSuccess: (
      state,
      action: PayloadAction<CardsTransactions[]>,
    ) => {
      state.cardsTransactionsIsLoading = false;
      state.values = action.payload;
      state.requestInProgress = false;
    },
    onSortCardsTransactionsList: (
      state,
      action: PayloadAction<{ name: string; direction: 'asc' | 'desc' }>,
    ) => {
      state.sort = {
        sortDir: action.payload.direction,
        sortProp: action.payload.name,
      };
      state.requestInProgress = true;
    },
    onSortCardsTransactionsListSuccess: (
      state,
      action: PayloadAction<CardsTransactions[]>,
    ) => {
      state.requestInProgress = false;
      state.values = action.payload;
    },
    onFilterCardsTransactionsList: (state, action: PayloadAction<Filters>) => {
      state.requestInProgress = true;
      state.filters = action.payload;
    },
    onFilterListCardsTransactionsSuccess: (
      state,
      action: PayloadAction<CardsTransactions[]>,
    ) => {
      state.requestInProgress = false;
      state.values = action.payload;
    },
    clearCardsState: (state) => {
      state.cardsTransactionsIsLoading = true;
      state.requestInProgress = false;
      state.values = undefined;
      state.customerType = undefined;
      state.isLoading = true;
    },
    getCards: (state) => {
      state.isLoading = true;
    },
    getCardsSuccess: (state, action: PayloadAction<CustomerCard[]>) => {
      state.isLoading = false;
      state.cards = action.payload;
    },
    onCardStatusChange: (
      state,
      action: PayloadAction<{ id: string; status: CardStatus }>,
    ) => {
      state.statusChangeInProgress = true;
      state.updatedCard = action.payload;
      state.changeStatusSuccess = false;
    },
    getCardInfo: (state, action: PayloadAction<string>) => {
      state.cardId = action.payload;
      state.cardInfoLoading = true;
    },
    getCardInfoSuccess: (state, action: PayloadAction<CustomerCard>) => {
      state.cardInfo = action.payload;
      state.cardInfoLoading = false;
    },
    onSubmitCard: (state, action: PayloadAction<OrderCardFormValues>) => {
      state.orderCardFormValues = action.payload;
      state.cardSubmitInProgress = true;
    },
    onSubmitCardSuccess: (state, action: PayloadAction<CustomerCard[]>) => {
      state.cardSubmitInProgress = false;

      state.cards = action.payload;

      state.cardSubmitSuccess = true;
    },

    onCardStatusChangeSuccess: (
      state,
      action: PayloadAction<CustomerCard[]>,
    ) => {
      state.statusChangeInProgress = false;
      state.updatedCard = undefined;
      state.cards = action.payload;
      state.changeStatusSuccess = true;
    },
    clearOrderCardForm: (state) => {
      state.orderCardFormValues = undefined;
      state.cardSubmitSuccess = false;
      state.cardId = undefined;
      state.cardInfo = undefined;
    },
  },
});

export const {
  clearCardsState,
  getCardsTransactions,
  getCardsTransactionsSuccess,
  onSortCardsTransactionsList,
  onSortCardsTransactionsListSuccess,
  onFilterCardsTransactionsList,
  onFilterListCardsTransactionsSuccess,
  getCards,
  getCardsSuccess,
  onCardStatusChange,
  onCardStatusChangeSuccess,
  getCardInfo,
  getCardInfoSuccess,
  onSubmitCard,
  clearOrderCardForm,
  onSubmitCardSuccess,
} = customerCardsSlice.actions;

export default customerCardsSlice.reducer;
